import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import AppHeader from './AppHeader';
import { financeAPI } from '../services/api';

const YEAR_STATUS = {
  open: 'Open year',
  closed: 'Closed year',
  not_started: 'Not started',
};

function apiError(err, fallback) {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return String(data.detail);
  return fallback;
}

function moneyLabel(amount) {
  if (amount == null || amount === '') return '';
  const text = String(amount);
  const negative = text.startsWith('-');
  return `${negative ? '−' : ''}$${text.replace('-', '')}`;
}

const AGING_LABELS = [
  ['0_30', '0–30 days'],
  ['31_60', '31–60 days'],
  ['61_90', '61–90 days'],
  ['91_120', '91–120 days'],
  ['over_120', '121 days or more'],
  ['not_billed', 'No bill date'],
  ['future', 'Billed after the as-of date'],
];

function agingText(aging) {
  if (!aging) return '';
  const parts = AGING_LABELS
    .filter(([key]) => aging[key] && aging[key].count)
    .map(([key, label]) => `${label}: ${aging[key].count}, open ${moneyLabel(aging[key].open)}`);
  if (!parts.length) return 'No receivables in this window.';
  return `Aging is per receivable. ${parts.join('. ')}.`;
}

function formatWhen(iso) {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return `${new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed)} ET`;
}

function figureText(measure) {
  if (!measure || measure.status === 'unavailable' || measure.display === 'Unavailable') {
    return 'Unavailable';
  }
  if (measure.amount != null) return moneyLabel(measure.amount);
  return measure.display ?? '';
}

function queryParams(searchParams) {
  const params = {};
  const year = searchParams.get('fiscal_year');
  const comparison = searchParams.get('comparison');
  const location = searchParams.get('location');
  const program = searchParams.get('program');
  const paySource = searchParams.get('pay_source');
  if (year) params.fiscal_year = year;
  if (comparison === 'same_elapsed_date' || comparison === 'full_year' || comparison === 'comparable' || comparison === 'full_year_projection') {
    params.comparison = comparison;
  }
  if (location) params.location = location;
  if (program) params.program = program;
  if (paySource) params.pay_source = paySource;
  return params;
}

function ProjectionLineChart({ title, points, selected, onSelect }) {
  const width = 720;
  const height = 210;
  const padL = 12;
  const padR = 12;
  const padT = 16;
  const padB = 32;
  const values = points.map((point) => Number(point.amount));
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const xAt = (index) => (
    padL + (points.length === 1 ? innerW / 2 : (innerW * index) / (points.length - 1))
  );
  const yAt = (value) => padT + ((max - value) / span) * innerH;
  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xAt(index)} ${yAt(Number(point.amount))}`)
    .join(' ');

  return (
    <Box>
      <Typography variant="h6" component="h2">{title}</Typography>
      {points.length === 0 ? (
        <Typography sx={{ mt: 1 }}>No projection lines in this period.</Typography>
      ) : (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title} width="100%">
            <path d={path} fill="none" stroke="#37474f" strokeWidth="2" />
            {points.map((point, index) => (
              <g
                key={point.period}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={() => onSelect(point.period)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(point.period);
                  }
                }}
              >
                <circle
                  cx={xAt(index)}
                  cy={yAt(Number(point.amount))}
                  r={selected === point.period ? 6 : 4}
                  fill="#37474f"
                />
                <text x={xAt(index)} y={height - 8} textAnchor="middle" fontSize="11" fill="#455a64">
                  {point.period.slice(5)}
                </text>
              </g>
            ))}
          </svg>
        </Box>
      )}
    </Box>
  );
}

function AmountBars({ title, rows, onSelect }) {
  const max = Math.max(1, ...rows.map((row) => Math.abs(Number(row.potential_mandated))));
  return (
    <Box>
      <Typography variant="h6" component="h2">{title}</Typography>
      {rows.length === 0 ? (
        <Typography sx={{ mt: 1 }}>No projection lines in this period.</Typography>
      ) : rows.map((row) => (
        <Button
          key={`${row.id ?? 'none'}-${row.name}`}
          onClick={() => onSelect(row.id)}
          disabled={row.id == null}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            textTransform: 'none',
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          <Box sx={{ width: '42%', pr: 1, textAlign: 'left' }}>{row.name}</Box>
          <Box sx={{ flex: 1, bgcolor: '#eceff1', mr: 1 }}>
            <Box sx={{
              width: `${(Math.abs(Number(row.potential_mandated)) / max) * 100}%`,
              bgcolor: '#37474f',
              height: 16,
            }}
            />
          </Box>
          <Box sx={{ minWidth: 110, textAlign: 'right' }}>{moneyLabel(row.potential_mandated)}</Box>
        </Button>
      ))}
    </Box>
  );
}

const RevenueDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [linesPayload, setLinesPayload] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [linesError, setLinesError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [openBar, setOpenBar] = useState('');
  const [paySourceDraft, setPaySourceDraft] = useState(searchParams.get('pay_source') || '');

  const params = useMemo(() => queryParams(searchParams), [searchParams]);
  const month = searchParams.get('month') || '';
  const lineId = searchParams.get('line') || '';
  const rawComparison = searchParams.get('comparison');
  const comparison = rawComparison === 'full_year' || rawComparison === 'full_year_projection'
    ? 'full_year'
    : 'same_elapsed_date';

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === '') next.delete(key);
    else next.set(key, String(value));
    if (key !== 'line') next.delete('line');
    if (key === 'fiscal_year' || key === 'comparison' || key === 'location' || key === 'program' || key === 'pay_source') {
      next.delete('month');
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    setPaySourceDraft(searchParams.get('pay_source') || '');
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setOpenBar('');
    Promise.all([
      financeAPI.overview(params),
      financeAPI.bridge(params),
    ]).then(([overviewRes, bridgeRes]) => {
      if (cancelled) return;
      setOverview(overviewRes.data);
      setBridge(bridgeRes.data);
    }).catch((err) => {
      if (cancelled) return;
      setOverview(null);
      setBridge(null);
      setError(apiError(err, 'The revenue report could not be loaded.'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [params]);

  useEffect(() => {
    if (!month) {
      setLinesPayload(null);
      setLinesError('');
      return undefined;
    }
    let cancelled = false;
    setLinesError('');
    financeAPI.lines({ ...params, month }).then((res) => {
      if (!cancelled) setLinesPayload(res.data);
    }).catch((err) => {
      if (cancelled) return;
      setLinesPayload(null);
      setLinesError(apiError(err, 'The calculation lines could not be loaded.'));
    });
    return () => { cancelled = true; };
  }, [params, month]);

  useEffect(() => {
    if (!lineId || !linesPayload) {
      setBreakdown(null);
      setDetailError('');
      return undefined;
    }
    const row = (linesPayload.lines || []).find((line) => String(line.id) === String(lineId));
    if (!row) return undefined;
    if (!row.student_key) {
      setBreakdown(null);
      setDetailError(linesPayload.student_level_reason || 'Student-level detail is withheld.');
      return undefined;
    }
    let cancelled = false;
    setDetailError('');
    financeAPI.line(lineId).then((res) => {
      if (!cancelled) setBreakdown(res.data);
    }).catch((err) => {
      if (cancelled) return;
      setBreakdown(null);
      setDetailError(apiError(err, 'The calculation could not be loaded.'));
    });
    return () => { cancelled = true; };
  }, [lineId, linesPayload]);

  const yearOptions = useMemo(() => {
    const ending = overview?.fiscal_year?.ending_year;
    const selected = Number(searchParams.get('fiscal_year') || ending);
    if (!selected) return [];
    const years = [];
    for (let year = selected - 3; year <= selected + 1; year += 1) years.push(year);
    return years;
  }, [overview, searchParams]);

  const monthlyPoints = (overview?.monthly || []).map((row) => ({
    period: row.period,
    amount: row.potential_mandated,
  }));

  const selectedYear = searchParams.get('fiscal_year') || (overview ? String(overview.fiscal_year.ending_year) : '');
  const chartTitle = overview
    ? `Potential mandated revenue by month (projection) — ${overview.fiscal_year.label}`
    : 'Potential mandated revenue by month (projection)';

  return (
    <>
      <AppHeader />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h4" component="h1">Revenue from mandates</Typography>
        <Typography sx={{ mt: 1, mb: 2 }}>
          Enrollment, potential mandated revenue, expected service revenue, earned revenue,
          billed revenue, and collected revenue stay separate.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Fiscal year"
            value={selectedYear}
            onChange={(event) => setParam('fiscal_year', event.target.value)}
            sx={{ minWidth: 160 }}
            disabled={!selectedYear}
          >
            {yearOptions.length === 0 && <MenuItem value="">Fiscal year</MenuItem>}
            {yearOptions.map((year) => (
              <MenuItem key={year} value={String(year)}>{`FY ending ${year}`}</MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={comparison}
            onChange={(_event, value) => {
              if (value) setParam('comparison', value);
            }}
            aria-label="Comparison"
          >
            <ToggleButton value="same_elapsed_date">Same elapsed date</ToggleButton>
            <ToggleButton value="full_year">Full-year projection</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            label="Pay source"
            value={paySourceDraft}
            onChange={(event) => setPaySourceDraft(event.target.value)}
            onBlur={() => {
              if (paySourceDraft !== (searchParams.get('pay_source') || '')) {
                setParam('pay_source', paySourceDraft.trim());
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                const res = await financeAPI.exportCsv(params);
                const blob = new Blob([res.data], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                const year = overview?.fiscal_year?.ending_year || 'year';
                link.href = url;
                link.download = `revenue-projection-fy${year}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                setError(apiError(err, 'The projection export could not be downloaded.'));
              }
            }}
          >
            Export projection
          </Button>
        </Box>

        {(searchParams.get('location') || searchParams.get('program')) && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {searchParams.get('location') && (
              <Chip
                label={`Location ${searchParams.get('location')}`}
                onDelete={() => setParam('location', '')}
              />
            )}
            {searchParams.get('program') && (
              <Chip
                label={`Program ${searchParams.get('program')}`}
                onDelete={() => setParam('program', '')}
              />
            )}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={120} />
            ))}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {overview && !loading && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              {overview.fiscal_year.label}
              {' · '}
              {YEAR_STATUS[overview.fiscal_year.status] || overview.fiscal_year.status}
              {' · '}
              {overview.comparison.note}
            </Alert>
            {overview.scope_note && <Alert severity="warning" sx={{ mb: 2 }}>{overview.scope_note}</Alert>}
            <Typography variant="body2" sx={{ mb: 2 }}>
              {overview.provenance.projection_version == null
                ? 'No projection has been run for this year.'
                : `Projection version ${overview.provenance.projection_version}, engine ${overview.provenance.engine_version}, rules ${overview.provenance.rules_hash}, snapshot ${formatWhen(overview.provenance.snapshot_extracted_at)}.`}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mb: 2 }}>
              {overview.measures.map((measure) => (
                <Paper key={measure.id} sx={{ p: 2 }} variant="outlined">
                  <Typography variant="overline">{measure.label}</Typography>
                  <Typography variant={measure.status === 'unavailable' ? 'h6' : 'h4'} component="p" sx={{ my: 1 }}>
                    {figureText(measure)}
                  </Typography>
                  {measure.status === 'partial' && <Chip size="small" label="Partial" sx={{ mb: 1 }} />}
                  <Typography variant="body2">{measure.reason}</Typography>
                </Paper>
              ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Typography variant="overline">Potential minus expected</Typography>
                <Typography variant="h6" component="p">Unavailable</Typography>
                <Typography variant="body2">{overview.gap_potential_to_expected.reason}</Typography>
              </Paper>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Typography variant="overline">{overview.enrollment_at_year_start.label}</Typography>
                <Typography variant="h6" component="p">{figureText(overview.enrollment_at_year_start)}</Typography>
                <Typography variant="body2">{overview.enrollment_at_year_start.reason}</Typography>
              </Paper>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Typography variant="overline">Enrollment movement</Typography>
                <Typography variant="h6" component="p">Unavailable</Typography>
                <Typography variant="body2">{overview.enrollment_movement.reason}</Typography>
              </Paper>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Typography variant="overline">Potential mandated revenue per student</Typography>
                <Typography variant="h6" component="p">
                  {overview.revenue_per_student.status === 'unavailable'
                    ? 'Unavailable'
                    : moneyLabel(overview.revenue_per_student.amount)}
                </Typography>
                {overview.revenue_per_student.status === 'partial' && <Chip size="small" label="Partial" sx={{ mb: 1 }} />}
                <Typography variant="body2">{overview.revenue_per_student.reason}</Typography>
              </Paper>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Typography variant="overline">Verified exposure</Typography>
                <Typography variant="h6" component="p">
                  {overview.verified_exposure.status === 'unavailable'
                    ? 'Unavailable'
                    : moneyLabel(overview.verified_exposure.amount)}
                </Typography>
                <Typography variant="body2">
                  {overview.verified_exposure.count != null ? `${overview.verified_exposure.count} mandates. ` : ''}
                  {overview.verified_exposure.reason}
                </Typography>
              </Paper>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Typography variant="overline">Estimated exposure</Typography>
                <Typography variant="h6" component="p">
                  {overview.estimated_exposure.count == null ? 'Unavailable' : `${overview.estimated_exposure.count} mandates`}
                </Typography>
                <Typography variant="body2">{overview.estimated_exposure.reason}</Typography>
              </Paper>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
              <ProjectionLineChart
                title={chartTitle}
                points={monthlyPoints}
                selected={month}
                onSelect={(period) => setParam('month', period === month ? '' : period)}
              />
              <TableContainer sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Potential mandated revenue (projection)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyPoints.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2}>No projection lines in this period.</TableCell>
                      </TableRow>
                    )}
                    {monthlyPoints.map((point) => (
                      <TableRow
                        key={point.period}
                        hover
                        selected={point.period === month}
                        onClick={() => setParam('month', point.period)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{point.period}</TableCell>
                        <TableCell align="right">{moneyLabel(point.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <Paper sx={{ p: 2 }} variant="outlined">
                <AmountBars
                  title={`Potential mandated revenue by location (projection) — ${overview.fiscal_year.label}`}
                  rows={overview.by_location}
                  onSelect={(id) => setParam('location', id)}
                />
              </Paper>
              <Paper sx={{ p: 2 }} variant="outlined">
                <AmountBars
                  title={`Potential mandated revenue by program (projection) — ${overview.fiscal_year.label}`}
                  rows={overview.by_program}
                  onSelect={(id) => setParam('program', id)}
                />
                <Typography variant="body2" sx={{ mt: 2 }}>{overview.like_for_like_note}</Typography>
              </Paper>
            </Box>

            {overview.prior_year && (
              <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
                <Typography variant="h6" component="h2">
                  {`Prior year potential mandated revenue (projection) — ${overview.prior_year.label}`}
                </Typography>
                <Typography variant="h5" component="p" sx={{ my: 1 }}>
                  {figureText(overview.prior_year.potential_mandated)}
                </Typography>
                <Typography variant="body2">{overview.prior_year.note}</Typography>
                {overview.prior_year.potential_mandated.reason && (
                  <Typography variant="body2">{overview.prior_year.potential_mandated.reason}</Typography>
                )}
              </Paper>
            )}

            <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
              <Typography variant="h6" component="h2">Projected against billed</Typography>
              <Typography sx={{ my: 1 }}>
                {overview.reconciliation.status === 'unavailable' ? 'Unavailable' : 'Reported, not accepted'}
              </Typography>
              <Typography variant="body2">{overview.reconciliation.reason}</Typography>
              {overview.reconciliation.difference != null && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {`Projected ${moneyLabel(overview.reconciliation.projected)}, billed ${moneyLabel(overview.reconciliation.billed)}, difference ${moneyLabel(overview.reconciliation.difference)}.`}
                </Typography>
              )}
              {overview.reconciliation.receivable_count != null && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {`${overview.reconciliation.receivable_count} receivables on ${overview.reconciliation.service_slot_count} enrollment and service dates (up to ${overview.reconciliation.max_receivables_on_one_service} on one date). ARType A adjustments stay in the net.`}
                </Typography>
              )}
              {overview.reconciliation.aging && (
                <Typography variant="body2" sx={{ mt: 1 }}>{agingText(overview.reconciliation.aging)}</Typography>
              )}
              {overview.reconciliation.gate === 'blocked_pending_tolerance' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No reconciliation tolerance has been agreed. This difference is not a pass.
                </Alert>
              )}
            </Paper>

            <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
              <Typography variant="h6" component="h2">
                {`Fiscal-year revenue bridge (projection) — ${overview.fiscal_year.label}`}
              </Typography>
              {bridge?.note && <Typography variant="body2" sx={{ mt: 1 }}>{bridge.note}</Typography>}
              {bridge && bridge.status !== 'ok' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {bridge.error || 'The bridge is not available.'}
                </Alert>
              )}
              {bridge && bridge.status === 'ok' && (
                <Box sx={{ mt: 2 }}>
                  <Typography>{`${bridge.opening.label}: ${moneyLabel(bridge.opening.amount)}`}</Typography>
                  {bridge.bars.map((bar) => {
                    const max = Math.max(1, ...bridge.bars.map((item) => Math.abs(Number(item.amount))));
                    return (
                      <Box key={bar.key} sx={{ my: 1 }}>
                        <Button
                          onClick={() => setOpenBar(openBar === bar.key ? '' : bar.key)}
                          sx={{ textTransform: 'none', color: 'text.primary', width: '100%', justifyContent: 'flex-start' }}
                        >
                          <Box sx={{ width: 180, textAlign: 'left' }}>{bar.label}</Box>
                          <Box sx={{ flex: 1, bgcolor: '#eceff1', mx: 1 }}>
                            <Box sx={{
                              width: `${(Math.abs(Number(bar.amount)) / max) * 100}%`,
                              bgcolor: '#37474f',
                              height: 16,
                            }}
                            />
                          </Box>
                          <Box>{moneyLabel(bar.amount)}</Box>
                        </Button>
                        {openBar === bar.key && (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Contributor</TableCell>
                                <TableCell align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(bar.contributors || []).length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={2}>No contributors.</TableCell>
                                </TableRow>
                              )}
                              {(bar.contributors || []).map((row, index) => (
                                <TableRow key={`${bar.key}-${index}`}>
                                  <TableCell>
                                    {row.student_detail === 'withheld'
                                      ? `Program ${row.program_key}. Student detail withheld.`
                                      : [row.student_key, row.program_key, row.service_type].filter(Boolean).join(' · ')}
                                  </TableCell>
                                  <TableCell align="right">{moneyLabel(row.amount)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    );
                  })}
                  <Typography>{`${bridge.closing.label}: ${moneyLabel(bridge.closing.amount)}`}</Typography>
                </Box>
              )}
            </Paper>

            {month && (
              <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
                <Typography variant="h6" component="h2">
                  {`Potential mandated revenue lines (projection) — ${month}`}
                </Typography>
                {linesPayload?.student_detail === 'withheld' && (
                  <Alert severity="info" sx={{ my: 1 }}>{linesPayload.student_level_reason}</Alert>
                )}
                {linesError && <Alert severity="error" sx={{ my: 1 }}>{linesError}</Alert>}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Program</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell align="right">Units</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell>Rate source</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(linesPayload?.lines || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8}>No lines in this month.</TableCell>
                        </TableRow>
                      )}
                      {(linesPayload?.lines || []).map((line) => (
                        <TableRow
                          key={line.id}
                          hover
                          onClick={() => setParam('line', line.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{line.period_start}</TableCell>
                          <TableCell>{line.location}</TableCell>
                          <TableCell>{line.program}</TableCell>
                          <TableCell>{line.service_type}</TableCell>
                          <TableCell align="right">{line.units}</TableCell>
                          <TableCell align="right">{moneyLabel(line.rate)}</TableCell>
                          <TableCell>
                            {line.locally_mapped ? `${line.rate_source} (locally mapped)` : line.rate_source}
                          </TableCell>
                          <TableCell align="right">{moneyLabel(line.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Paper sx={{ p: 2 }} variant="outlined">
              <Typography variant="h6" component="h2">Open questions</Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                Q1 and Q2 can move every figure on this page. Neither is answered here.
              </Typography>
              {(overview.open_questions || []).map((item) => (
                <Box key={item.id} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2">{`${item.id} · ${item.owner}`}</Typography>
                  <Typography variant="body2">{item.question}</Typography>
                </Box>
              ))}
            </Paper>
          </>
        )}

        <Dialog open={Boolean(lineId)} onClose={() => setParam('line', '')} maxWidth="sm" fullWidth>
          <DialogTitle>Calculation breakdown (projection)</DialogTitle>
          <DialogContent>
            {detailError && <Alert severity="info" sx={{ mb: 2 }}>{detailError}</Alert>}
            {breakdown && (
              <Box>
                <Typography>{breakdown.measure}</Typography>
                <Typography sx={{ mt: 1 }}>{`Student ${breakdown.student_key}`}</Typography>
                <Typography>{`Mandate ${breakdown.mandate_key}`}</Typography>
                <Typography>{`Authorization ${breakdown.authorization_number || 'blank'}`}</Typography>
                <Typography>{`${breakdown.program} · ${breakdown.location} · ${breakdown.service_type}`}</Typography>
                <Typography>{`${breakdown.period_start} to ${breakdown.period_end}`}</Typography>
                <Typography sx={{ mt: 1 }}>{breakdown.arithmetic}</Typography>
                <Typography>{`Rate source: ${breakdown.rate_source}. ${breakdown.rate_origin}.`}</Typography>
                <Typography sx={{ mt: 1 }}>{breakdown.basis}</Typography>
                {breakdown.receivable_trail && (
                  <Typography sx={{ mt: 1 }}>
                    {breakdown.receivable_trail.status === 'unavailable'
                      ? breakdown.receivable_trail.reason
                      : `${breakdown.receivable_trail.receivable_count} receivables on this enrollment, billed ${moneyLabel(breakdown.receivable_trail.billed)}. ${breakdown.receivable_trail.note || ''}`}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
};

export default RevenueDashboard;
